<template>
  <div
    class="list-picker"
    :class="{
      'list-picker--as-title': asTitle,
      'list-picker--as-buttons': !asList,
      'list-picker--as-list': asList,
      'list-picker--as-toggle': asToggle,
    }"
  >
    <p
      v-if="!asTitle"
      class="list-picker__label text-extra-small text-md-big text-secondary text-color-tertiary text-color-md-secondary mb-0-6"
    >
      {{ label }}
    </p>

    <button v-if="asList" class="list-picker__button" @click="toggleOpen">
      <span
        class="inner d-flex align-center justify-center justify-md-space-between text-color-tertiary text-color-md-secondary text-secondary text-extra-small text-md-base w-600"
      >
        <span
          ><span v-if="asTitle && title">{{ title }} - </span
          >{{ selectedValues }}</span
        >

        <icon name="carret-down" />
      </span>
    </button>

    <transition @before-enter="onBeforeEnter" @enter="onEnter" @leave="onLeave">
      <div v-if="!asList || open" class="list-picker__items">
        <ul>
          <li
            v-for="(v, index) in values"
            :key="index"
            class="text-secondary text-color-secondary text-small text-md-base w-600"
            :class="{
              'list-picker__item--active': value && v.value === value.value,
            }"
          >
            <nuxt-link
              v-if="asLink && v.to"
              :to="v.to"
              class="link--underline"
              :class="{ active: value && v.value === value.value }"
            >
              <i class="list-picker__item-icon">
                <icon name="check" />
              </i>
              <span>{{ v.label }}</span>
            </nuxt-link>
            <button
              v-else
              class="list-picker__item d-block"
              @click="onClick(v)"
            >
              <i class="list-picker__item-icon">
                <icon name="check" />
              </i>
              <span>{{ v.label }}</span>
            </button>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
import Icon from '@/components/icons/Icon'

export default {
  name: 'ListPicker',
  components: {
    Icon,
  },
  props: {
    value: {
      type: [Object, Array],
      default: null,
    },
    values: {
      type: Array,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    asLink: {
      type: Boolean,
      default: false,
    },
    asTitle: {
      type: Boolean,
      default: false,
    },
    asToggle: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: null,
    },
  },

  data: () => ({
    open: false,
  }),

  computed: {
    asList() {
      return this.values.length > 3 || this.asTitle
    },
    selectedValues() {
      if (!this.value) {
        return this.label
      }

      if (Array.isArray(this.value)) {
        return this.value.map((v) => v.label).join(', ')
      }
      return this.value ? this.value.label : ''
    },
  },

  beforeDestroy() {
    window.removeEventListener('click', this.onBodyClose)
  },

  methods: {
    toggleOpen() {
      this.open = !this.open

      window.addEventListener('click', this.onBodyClose)
    },
    onClick(v) {
      this.$emit('input', v.value, v)
      this.toggleOpen()
    },
    onBodyClose(event) {
      const target = event.target
      const isClickInside = this.$el.contains(target)

      if (!isClickInside) {
        this.open = false
        window.removeEventListener('click', this.onBodyClose)
      }
    },
    async onBeforeEnter(el) {
      if (this.asList) {
        const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

        gsap.set(el, {
          opacity: 0,
          height: 0,
          y: -10,
          overflow: 'hidden',
        })
      }
    },
    async onEnter(el, done) {
      if (!this.asList) {
        return done()
      }

      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      gsap.to(el, {
        opacity: 1,
        height: el.scrollHeight,
        y: 0,
        duration: 0.3,
        ease: 'power2.out',
        onComplete: () => {
          gsap.set(el, { overflow: 'visible' })
          return done()
        },
      })
    },
    async onLeave(el, done) {
      if (!this.asList) {
        return done()
      }

      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      gsap.to(el, {
        opacity: 0,
        height: 0,
        y: -10,
        duration: 0.3,
        overflow: 'hidden',
        ease: 'power2.in',
        onComplete: () => {
          return done()
        },
      })
    },
  },
}
</script>

<style lang="scss">
.list-picker {
  position: relative;
}

.list-picker__label {
  @include mq($until: tablet) {
    line-height: var(--small-font-size);
  }
}

.list-picker__button {
  border: none;
  background-color: transparent;
  width: 100%;
  margin: 0;
  padding: rem(1px) 0;
  cursor: pointer;

  .inner {
    letter-spacing: 0.02em;

    @include mq($until: tablet) {
      background-color: var(--tertiary-color);
      color: var(--secondary-color);
      border-radius: 4px;
      padding: 0 calc(var(--spacing) * 0.6);
      gap: calc(var(--spacing) * 0.6);
      min-height: rem(32px);
    }

    @include mq($from: tablet) {
      background-color: white;
      min-height: rem(50px);
      padding: rem(1px) var(--spacing) 0;
      box-shadow: inset 0 0 0 rem(1px) RGB(0 46 93 / 0.2);
    }
  }
}

.list-picker__items {
  padding: calc(var(--spacing) * 0.4) 0;

  @include mq($until: tablet) {
    position: absolute;
    min-width: 100%;
    left: 0;
    top: 50%;
    z-index: 10;
    transform: translateY(-50%);
    border-radius: 4px;
  }

  ul {
    margin-bottom: 0;
    padding-left: 0;
  }

  li {
    position: relative;

    &::before {
      content: none;
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      opacity: 0;
      background-color: var(--tertiary-color);
      pointer-events: none;
      z-index: 1;
      transition: all 0.3s var(--asphalte-animation-function), border-width 0s;
    }

    > * {
      position: relative;
      display: block;
      padding: calc(var(--spacing) * 0.4) var(--spacing);
      z-index: 2;
    }

    @include on-hover-and-focus {
      &::after {
        opacity: 1;
      }
    }
  }

  button {
    background: transparent;
    border: none;
    width: 100%;
    cursor: pointer;
    text-align: left;
    font-family: var(--secondary-font-family);
  }
}

.list-picker__item-icon {
  display: none;
  width: rem(20px);
  height: rem(20px);
  border: 1px solid var(--secondary-color-3);
  background-color: white;
  border-radius: 50%;
  position: absolute;
  left: var(--spacing);
  top: 50%;
  transform: translateY(-50%);

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: rem(16px);
    height: rem(16px);
    background-color: var(--secondary-color);
    border-radius: 50%;
    transform: translate(-50%, -50%);
    opacity: 0;
    z-index: 1;
  }

  .icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: rem(16px);
    height: rem(16px);
    color: var(--tertiary-color);
    opacity: 0;
    z-index: 2;
  }

  .list-picker__item--active & {
    &::before,
    .icon {
      opacity: 1;
    }
  }
}

.list-picker--as-list {
  .list-picker__items {
    background-color: white;
    border: solid RGB(0 46 93 / 0.2);
    border-width: 0 rem(1px) rem(1px);
  }
}

.list-picker--as-buttons {
  .list-picker__items {
    ul {
      display: flex;
      align-items: center;
      gap: calc(var(--spacing) * 0.6);
    }

    li {
      flex: 1 0 auto;
      background-color: var(--tertiary-color);
      border: rem(1px) solid RGB(0 46 93 / 0.2);
      border-radius: 2px;

      &::after {
        border: 1px solid var(--secondary-color);
        background: transparent;
        border-radius: 2px;
        inset: -1px;
      }

      > * {
        padding: var(--spacing) var(--spacing) var(--spacing)
          calc(var(--spacing) * 2);
        text-align: center;
      }
    }

    .list-picker__item--active {
      &::after {
        opacity: 1;
        border-width: 2px;
      }
    }
  }

  .list-picker__item-icon {
    display: block;
  }

  .link--underline {
    --link-underline-transform: scaleX(0);
  }
}

.list-picker--as-title {
  display: flex;
  align-items: center;
  position: relative;

  .list-picker__button {
    .inner {
      font-size: var(--base-font-size);
      margin-left: calc(var(--spacing) * -0.4);
      padding: 0 calc(var(--spacing) * 0.4);
      border: none;
      min-height: 0;
      box-shadow: none;
      gap: calc(var(--spacing) * 0.4);

      .purchase-sticky--tabs & {
        color: var(--secondary-color);
      }
    }
  }

  .list-picker__items {
    border-width: rem(1px);
    position: absolute;
    z-index: 10;
    top: 50%;
    right: 0;
    transform: translateY(-50%);

    li > * {
      white-space: nowrap;
    }
  }
}

.list-picker--as-toggle {
  .list-picker__label {
    @include visually-hidden;
  }

  .list-picker__items {
    transform: none;
    position: static;
    padding: rem(2px);
    background-color: RGB(0 0 0 / 0.2);
    border-radius: 4px;
  }

  &.list-picker--as-buttons {
    ul {
      gap: 0;
    }

    .list-picker__items li {
      background-color: transparent;
      border: none;

      > * {
        font-size: var(--extra-small-font-size);
        color: var(--tertiary-color);
        padding: calc(var(--spacing) * 0.2) calc(var(--spacing) * 0.6);
        border-radius: 2px;
      }

      &::after {
        content: none;
      }
    }

    .list-picker__item-icon {
      display: none;
    }

    .list-picker__item--active {
      > * {
        color: var(--secondary-color);
        background-color: RGB(255 255 255 / 0.4);
      }
    }
  }
}
</style>
